import { Outlet, useOutletContext } from 'react-router-dom';
import { LeftMenuBar } from './MenuBar/LeftMenuBar';
import { BottomMenuBar } from './MenuBar/BottomMenuBar';
import { MobileMoreModal } from './MobileMoreModal';
import Navbar from './Navbar/Navbar';
import AdminBar from './AdminBar/AdminBar';
import { LayoutSubscriptionBanner } from './LayoutSubscriptionBanner';

import { StanLoader } from '@/components';
import { AskStanley, useAskStanley } from '@/features/ask-stanley';
import { useGlobalStore, useNavigationStore } from '@/stores';
import { cn } from '@/lib';
import type { MainLayoutContext } from './MainLayout.types';

/**
 * Main layout for authenticated pages with sidebar and navbar.
 * Automatically wraps all pages defined under this layout in the router.
 *
 * By default, shows navbar and sidebar/bottom menu. Pages are automatically wrapped -
 * no need to import or use this component directly in your page files.
 *
 * To customize (hide navbar/sidebar), create a wrapper component:
 * @example
 * ```tsx
 * // Create a layout wrapper component
 * const EditorLayoutWrapper = () => {
 *   return <Outlet context={{ showNavbar: false }} />;
 * };
 *
 * // Use in router configuration
 * { path: '/editor', element: <EditorLayoutWrapper />, children: [...] }
 * ```
 */
export const MainLayout = () => {
    const isLoading = useGlobalStore(state => state.isPageLoading());
    const context = useOutletContext<MainLayoutContext>();
    const { isOpen: isStanleyOpen } = useAskStanley();
    const { isMobileMoreModalOpen, closeMobileMoreModal } = useNavigationStore();

    // Default to showing navbar and menubar unless explicitly disabled
    const showNavbar = context?.showNavbar !== false;
    const showMenuBar = context?.showMenuBar !== false;

    return (
        <div className='bg-canvas relative flex h-dvh'>
            {showMenuBar && <LeftMenuBar />}

            <div
                className={cn(
                    'no-scrollbar relative flex flex-col transition-[width,margin] duration-300 ease-in-out',
                    'lg:ml-[var(--spacing-left-menu-bar)]',
                    isStanleyOpen
                        ? 'lg:mr-[var(--spacing-ask-stanley-lg)] lg:w-[calc(100%-var(--spacing-left-menu-bar)-var(--spacing-ask-stanley-lg))] 2xl:mr-[var(--spacing-ask-stanley-2xl)] 2xl:w-[calc(100%-var(--spacing-left-menu-bar)-var(--spacing-ask-stanley-2xl))]'
                        : 'w-full'
                )}
            >
                {showNavbar && (
                    <div className='bg-canvas sticky top-0 z-20'>
                        <AdminBar />
                        <Navbar />
                    </div>
                )}
                <div className='no-scrollbar mb-space-60 lg:mb-space-0 relative w-full flex-1 overflow-x-hidden overflow-y-auto p-4 max-lg:pb-20 lg:p-6'>
                    <LayoutSubscriptionBanner />
                    <Outlet context={context} />
                </div>
                {showMenuBar && <BottomMenuBar className='flex lg:hidden' />}
                {isLoading && (
                    <div className='bg-canvas absolute inset-0 z-20 flex items-center justify-center backdrop-blur-sm max-lg:bottom-[var(--spacing-bottom-menu-bar)]'>
                        <StanLoader className='mt-auto! h-full! items-center' dotSize={20} />
                    </div>
                )}
            </div>

            {/* Ask Stanley Chat Interface */}
            <AskStanley hideFab={isMobileMoreModalOpen} />

            {/* Mobile More Options Modal */}
            <MobileMoreModal isOpen={isMobileMoreModalOpen} onClose={closeMobileMoreModal} />
        </div>
    );
};
